import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { GridApi } from 'ag-grid-community';
import {
    createRoyaltiesBarSeries,
    generateRoyaltiesChartData
} from 'src/components/Analytics/utils/royaltiesChartData';
import { RoyaltiesGridData } from 'src/hooks/analytics';
import { IChartData } from 'src/types/IAnalytics';

import { MetricsLineChart } from '../../MetricsCharts';
import { MetricsGrid } from '../../MetricsGrid';
import { getUniqueMonths, transformRoyaltiesToPivot } from '../../utils/dataTransformation';
import { createRoyaltiesPivotColDefs, royaltiesPivotColDefsEmpty } from './royaltiesPivotColDefs';

export interface RoyaltiesViewPivotProps {
    filterText?: string;
    data?: RoyaltiesGridData[];
    loading?: boolean;
    error?: string;
    isDataFullyLoaded?: boolean;
    completionPercentage?: number;
    selectedMonths?: string[]; // Array of selected months in YYYYMM format
}

export const RoyaltiesViewPivot: React.FC<RoyaltiesViewPivotProps> = ({
    filterText = '',
    data = [],
    loading = false,
    error,
    isDataFullyLoaded = true,
    completionPercentage = 100,
    selectedMonths = []
}) => {
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const [filteredTerritories, setFilteredTerritories] = useState<string[]>([]);
    const [filteredVendors, setFilteredVendors] = useState<string[]>([]);

    // Helper function to get loading months from completion percentage
    const getLoadingMonths = (): string[] => {
        if (isDataFullyLoaded || selectedMonths.length === 0) return [];

        const loadedCount = Math.floor((completionPercentage / 100) * selectedMonths.length);
        return selectedMonths.slice(loadedCount);
    };

    // Transform data to pivot format
    const pivotData = useMemo(() => transformRoyaltiesToPivot(data), [data]);

    // Generate chart data (same as regular view)
    const [chartData, setChartData] = useState<IChartData[]>([]);

    // Memoize chart data generation to avoid unnecessary recalculations
    const memoizedChartData = useMemo(() => {
        // Generate chart data even with empty data - AG Charts will show loading overlay
        const { barChartData, lineChartData, availableVendors } = generateRoyaltiesChartData({
            data: data,
            filteredTerritories: filteredTerritories.length > 0 ? filteredTerritories : undefined,
            filteredVendors: filteredVendors.length > 0 ? filteredVendors : undefined
        });

        // Create filter description for titles
        let filterDesc = '';
        if (filteredTerritories.length > 0 || filteredVendors.length > 0) {
            const parts = [];
            if (filteredTerritories.length > 0) {
                parts.push(`Territories: ${filteredTerritories.join(', ')}`);
            }
            if (filteredVendors.length > 0) {
                parts.push(`Vendors: ${filteredVendors.join(', ')}`);
            }
            filterDesc = `Filtered: ${parts.join('; ')}`;
        }

        // Create chart series
        const barSeries = createRoyaltiesBarSeries(availableVendors);

        return [
            {
                type: 'bar' as const,
                title: 'Royalties per Month (USD)',
                subtitle: filterDesc,
                data: barChartData,
                xKey: 'month',
                series: barSeries
            },
            {
                type: 'line' as const,
                title: 'Royalties Trending by Vendor (USD)',
                subtitle: filterDesc,
                data: lineChartData,
                xKey: 'month',
                yKeys: availableVendors
            }
        ];
    }, [data, filteredTerritories, filteredVendors]);

    // Update chart data when memoized data changes
    useEffect(() => {
        setChartData(memoizedChartData);
    }, [memoizedChartData]);

    // Get unique months for column definitions
    const availableMonths = useMemo(() => getUniqueMonths(data), [data]);

    // Generate column definitions based on available months
    const columnDefs = useMemo(() => {
        return availableMonths.length > 0 ? createRoyaltiesPivotColDefs(availableMonths) : royaltiesPivotColDefsEmpty;
    }, [availableMonths]);

    const autoGroupColumnDef = useMemo(() => {
        return {
            field: 'territory',
            minWidth: 120,
            pinned: true
        };
    }, []);

    // Generate default sort model for pivot view (sort by first month column descending)
    const defaultSortModel = useMemo(() => {
        if (availableMonths.length > 0) {
            // Sort by the first (most recent) month column in descending order
            const firstMonth = availableMonths[0];
            return [{ colId: firstMonth, sort: 'desc' as const }];
        }
        return [];
    }, [availableMonths]);

    // Handle grid ready event
    const handleGridReady = (api: GridApi) => {
        setAgGridApi(api);
    };

    // Memoize filter change handler to prevent unnecessary re-renders
    const handleFilterChanged = useCallback(() => {
        if (!agGridApi) return;

        // Get current filter model
        const filterModel = agGridApi.getFilterModel();

        // Extract filtered territories
        const territoryFilter = filterModel['territory'];
        const territories = territoryFilter && territoryFilter.values ? territoryFilter.values : [];
        setFilteredTerritories(territories);

        // Extract filtered vendors
        const vendorFilter = filterModel['vendor'];
        const vendors = vendorFilter && vendorFilter.values ? vendorFilter.values : [];
        setFilteredVendors(vendors);
    }, [agGridApi]);

    // Show no data state
    if (isDataFullyLoaded && data.length === 0) {
        return (
            <Column heights="fill" alignmentHorizontal="center" alignmentVertical="center">
                <Text>No royalties data available</Text>
            </Column>
        );
    }

    // Show no data for filters state
    if (chartData.length === 0 && (filteredTerritories.length > 0 || filteredVendors.length > 0)) {
        return (
            <Column heights={['fit', 'fit', 'fill']}>
                {!isDataFullyLoaded && (
                    <Row width="100%" alignmentHorizontal="center" spacing="200">
                        <Text type="b100" color="secondary">
                            Loading data... ({completionPercentage}%)
                        </Text>
                    </Row>
                )}
                <Row width="100%" widths="fill" alignmentVertical="top">
                    <Column heights="fill" alignmentHorizontal="center" alignmentVertical="center">
                        <Text>No data available for current filters</Text>
                    </Column>
                </Row>
                <MetricsGrid
                    columnDefs={columnDefs}
                    rowData={pivotData}
                    filterText={filterText}
                    onGridReady={handleGridReady}
                    onFilterChanged={handleFilterChanged}
                    defaultSortModel={defaultSortModel}
                    autoGroupColumnDef={autoGroupColumnDef}
                    isDataFullyLoaded={isDataFullyLoaded}
                />
            </Column>
        );
    }

    return (
        <Column heights={['fit', 'fit', 'fill']}>
            {!isDataFullyLoaded && (
                <Row width="100%" alignmentHorizontal="center" spacing="200">
                    <Text type="b100" color="secondary">
                        Loading data... ({completionPercentage}%)
                    </Text>
                </Row>
            )}
            <Row width="100%" widths="fill" alignmentVertical="top">
                {chartData.map((chart, index) => {
                    const loadingMonths = getLoadingMonths();

                    if (chart.type === 'line') {
                        return (
                            <MetricsLineChart
                                key={index}
                                title={chart.title}
                                subtitle={chart.subtitle}
                                data={chart.data}
                                xKey={chart.xKey || ''}
                                yKeys={chart.yKeys || []}
                                metricsType="royalties"
                                isDataPartial={!isDataFullyLoaded}
                                completionPercentage={completionPercentage}
                                loadingMonths={loadingMonths}
                                loading={loading}
                            />
                        );
                    }
                    return null;
                })}
            </Row>
            <MetricsGrid
                columnDefs={columnDefs}
                rowData={pivotData}
                filterText={filterText}
                onGridReady={handleGridReady}
                onFilterChanged={handleFilterChanged}
                defaultSortModel={defaultSortModel}
                autoGroupColumnDef={autoGroupColumnDef}
                isDataFullyLoaded={isDataFullyLoaded}
            />
        </Column>
    );
};
